import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiLock, FiUser, FiEye, FiEyeOff, FiLogIn } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import Logo from '../components/Logo';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [debugMessages, setDebugMessages] = useState([]);

  const addDebugMessage = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugMessages(prev => [...prev, `[${timestamp}] ${message}`]);
    console.log(`[DEBUG ${timestamp}] ${message}`);
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const from = location.state?.from?.pathname || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDebugMessages([]);
    addDebugMessage('Form submitted');
    addDebugMessage(`Username: ${formData.username}`);
    addDebugMessage(`API URL: ${process.env.REACT_APP_API_URL || 'https://api.exploreandwin.com'}/api/cms/auth/login`);
    console.log('[LOGIN PAGE] Form submitted');
    console.log('[LOGIN PAGE] Form data:', formData);
    setIsLoading(true);

    try {
      addDebugMessage('Sending login request...');
      console.log('[LOGIN PAGE] Calling login function...');
      const result = await login(formData.username, formData.password);
      addDebugMessage(`Response received: ${result.success ? 'SUCCESS' : 'FAILED'}`);
      console.log('[LOGIN PAGE] Login result:', result);

      if (result.success) {
        addDebugMessage(`User: ${result.user.username} (${result.user.role})`);
        addDebugMessage('Login successful! Redirecting...');
        console.log('[LOGIN PAGE] Login successful, navigating to:', from);

        // Check localStorage right after login
        const checkToken = localStorage.getItem('cms_accessToken');
        const checkUser = localStorage.getItem('cms_user');
        console.log('[LOGIN PAGE] LocalStorage check - Token:', checkToken ? 'exists' : 'null');
        console.log('[LOGIN PAGE] LocalStorage check - User:', checkUser ? 'exists' : 'null');

        toast.success(`Welcome back, ${result.user.fullName || result.user.username}!`);
        setTimeout(() => {
          console.log('[LOGIN PAGE] About to navigate, checking storage again...');
          const beforeNavToken = localStorage.getItem('cms_accessToken');
          console.log('[LOGIN PAGE] Before navigate - Token:', beforeNavToken ? 'exists' : 'null');
          navigate(from, { replace: true });
        }, 500);
      } else {
        addDebugMessage(`Error: ${result.error}`);
        if (result.details) {
          addDebugMessage(`Status: ${result.details.status || 'Unknown'}`);
          addDebugMessage(`Status Text: ${result.details.statusText || 'Unknown'}`);
          if (result.details.data) {
            addDebugMessage(`Response: ${JSON.stringify(result.details.data)}`);
          }
        }
        console.log('[LOGIN PAGE] Login failed:', result.error);
        console.log('[LOGIN PAGE] Error details:', result.details);
        toast.error(result.error);
        setFormData(prev => ({ ...prev, password: '' }));
      }
    } catch (error) {
      addDebugMessage(`ERROR: ${error.message}`);
      if (error.response) {
        addDebugMessage(`Response status: ${error.response.status}`);
        addDebugMessage(`Response data: ${JSON.stringify(error.response.data)}`);
      }
      console.error('[LOGIN PAGE] Unexpected error:', error);
      console.error('[LOGIN PAGE] Error stack:', error.stack);
      toast.error('An unexpected error occurred: ' + error.message);
      setFormData(prev => ({ ...prev, password: '' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 w-full h-full bg-background">
      <div className="absolute inset-0 overflow-auto">
        <div className="min-h-full flex flex-col justify-center py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/images/gamimap-logo.png"
            alt="gamimap"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
          {t('cms.login.title', 'Content Management System')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-light">
          {t('cms.login.subtitle', 'Sign in to access the admin dashboard')}
        </p>
      </div>

      <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 px-4 sm:py-8 shadow-lg rounded-lg sm:px-10 border border-gray-200">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-text-primary">
                {t('cms.login.username', 'Username or Email')}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-text-secondary" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-3 py-2 bg-background border border-text-secondary/30 rounded-md placeholder-text-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your username or email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-primary">
                {t('cms.login.password', 'Password')}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-text-secondary" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="appearance-none block w-full pl-10 pr-10 py-2 bg-background border border-text-secondary/30 rounded-md placeholder-text-secondary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-text-secondary hover:text-text-primary focus:outline-none transition-colors"
                  >
                    {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !formData.username || !formData.password}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <FiLogIn className="h-5 w-5 text-white/80 group-hover:text-white group-disabled:text-gray-300" />
                </span>
                {isLoading ? t('cms.login.signingIn', 'Signing in...') : t('cms.login.signIn', 'Sign in')}
              </button>
            </div>
          </form>

          {/* Test Connection Button */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={async () => {
                setDebugMessages([]);
                addDebugMessage('Testing API connection...');
                try {
                  const response = await fetch('https://api.exploreandwin.com/health');
                  const data = await response.json();
                  addDebugMessage(`API Status: ${data.status}`);
                  addDebugMessage(`Database: ${data.database?.connected ? 'Connected' : 'Not connected'}`);
                  addDebugMessage(`Server: ${data.server?.domain || 'Unknown'}`);
                } catch (error) {
                  addDebugMessage(`Connection failed: ${error.message}`);
                }
              }}
              className="text-text-light hover:text-text-primary text-sm underline"
            >
              Test API Connection
            </button>
          </div>

          {/* Debug Panel */}
          {debugMessages.length > 0 && (
            <div className="mt-6 p-4 bg-background-dark rounded-lg border border-text-secondary/30">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-text-primary font-semibold">Debug Console</h3>
                <button
                  onClick={() => setDebugMessages([])}
                  className="text-text-light hover:text-text-primary text-sm"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-1 text-xs font-mono text-text-light max-h-40 overflow-y-auto">
                {debugMessages.map((msg, idx) => (
                  <div key={idx} className="break-all">
                    {msg}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default Login;